/**
 * Every limit the producer board is held to, in one place.
 *
 * The server refuses a board that breaks any of these (`validateBoardState` in
 * `workers/src/lib/board-materialise.ts`, which reads them from here), so the
 * board UI uses the same numbers for its `maxLength`s and its counters. A field
 * that says "60 / 60" is a field the next save will accept.
 */
import { PROMISE_MAX_CHARS, TITLE_MAX_CHARS } from "./config";

/** How long one piece of card copy may be. */
export const BOARD_CAPS = {
  /** The page title, and every alternative title. */
  title: TITLE_MAX_CHARS,
  /** The description, and every alternative description. */
  desc: PROMISE_MAX_CHARS,
  /** A line of card copy: a heading's line, a chapter's sub, a form button's row. */
  line: 220,
  /** A paragraph: a section, an answer, a quote, an offer's page text. */
  prose: 500,
  /** A stored asset key, or one `[MM:SS]` source. */
  key: 300,
  /** A card's own label, a person's name, a section heading. */
  label: 120,
  /** A card id. */
  id: 80,
  /** The sign-up button, and one form field's key. */
  btn: 60,
  /** The offer's button. */
  offerBtn: 120,
  /** A price, as the host writes it. */
  price: 60,
  /** One number on the Numbers card. */
  stat: 40,
  /** A bonus's title. */
  bonusTitle: 160,
  /** A handout, as it is shown. */
  handoutName: 200,
  /** One poll option. */
  option: 120,
  /** One emoji (a few code points). */
  emoji: 16,
  /** The host's one line, on the Host card. */
  hostLine: 220,
  /**
   * A follow-up email's subject. The producer refuses a longer one
   * (`SUBJECT_MAX_CHARS`, `workers/src/ai/producer/grounding.ts`), so the field
   * stops where the producer does.
   */
  mailSubject: 70,
} as const;

/** How many of a thing one card may hold. */
export const BOARD_LIMITS = {
  cardsPerLane: 120,
  alts: 10,
  chapters: 60,
  faq: 40,
  steps: 12,
  formFields: 20,
  testimonials: 20,
  stats: 12,
  logos: 20,
  speakers: 12,
  tiers: 8,
  bonuses: 12,
  options: 8,
  emojis: 12,
  sources: 10,
} as const;

/**
 * The biggest recording the upload path can carry: the multipart ceiling of
 * 10,000 parts at the part size the reserve hands out
 * (`PART_SIZE` / `MAX_PART_NUMBER`, `workers/src/video/upload.ts`).
 */

const UPLOAD_PART_SIZE = 10 * 1024 * 1024;
const UPLOAD_MAX_PARTS = 10_000;
export const UPLOAD_MAX_BYTES = UPLOAD_PART_SIZE * UPLOAD_MAX_PARTS;

/** That ceiling in the words a host reads. */
export function uploadLimitLabel(): string {
  return `${Math.floor(UPLOAD_MAX_BYTES / 1024 ** 3)} GB`;
}
